import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, categoryText, type LotSummary, type Product } from '../api';
import { ScanField } from '../components/ScanField';
import { ErrorBox, Field, Loading, Money, Success, Val, fmtDate } from '../components/ui';
import { useSettings } from '../hooks';
import { WebInfo } from '../components/WebInfo';
import { RetailPriceCard } from '../components/RetailPrice';

export function ProductDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['product', id], queryFn: () => api<{ product: Product }>(`/products/${id}`).then((r) => r.product) });
  const lots = useQuery({ queryKey: ['lots', 'product', id], queryFn: () => api<{ lots: LotSummary[]; total: number }>(`/lots?status=all&productId=${id}`) });
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [bcError, setBcError] = useState<unknown>(null);
  const [bcBusy, setBcBusy] = useState(false);
  const setProduct = (p: Product) => qc.setQueryData(['product', id], p);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const p = q.data!;

  const addBarcode = async (code: string) => {
    setBcBusy(true);
    setBcError(null);
    setMsg(null);
    try {
      setProduct((await api<{ product: Product }>(`/products/${p.id}/barcodes`, { body: { barcode: code } })).product);
      setMsg(`Barcode ${code} linked.`);
    } catch (e) {
      setBcError(e);
    } finally {
      setBcBusy(false);
    }
  };
  const removeBarcode = async (bid: string, code: string) => {
    if (!confirm(`Unlink barcode ${code} from this product?`)) return;
    try {
      setProduct((await api<{ product: Product }>(`/products/${p.id}/barcodes/${bid}`, { method: 'DELETE' })).product);
    } catch (e) {
      setBcError(e);
    }
  };

  return (
    <div className="narrow">
      <h1>
        Item <span className="mono">{p.itemNumber}</span>
      </h1>
      <p>
        <span className="muted small">Category </span>
        <Val v={categoryText(p.mainCategory, p.realSubcategory)} missing="none yet" />
        {p.mainCategory && !p.realSubcategory && <span className="muted small"> (subcategory not known yet)</span>}
      </p>
      <RetailPriceCard productId={p.id} />
      <section className="card">
        <h2>{p.source === 'web' ? 'Source values' : 'Imported manifest values'}</h2>
        {p.source === 'csv_import' ? (
          <>
            <p className="muted small">
              From <Link to={`/imports/${p.importId}`}>{p.importFilename ?? `import #${p.importId}`}</Link>, {p.importSheet ? `sheet "${p.importSheet}", ` : ''}row{' '}
              {p.importRowNumber}. These values are kept exactly as supplied and are not editable.
            </p>
            <dl className="dl">
              <dt>Description</dt>
              <dd>
                <Val v={p.manifestTitle} missing="blank in file" />
              </dd>
              <dt>Retail price</dt>
              <dd>
                <Money amount={p.manifestPrice} currency={p.manifestCurrency} />
              </dd>
              <dt>Sell price</dt>
              <dd>
                <Money amount={p.manifestSellPrice ?? null} currency={p.manifestCurrency} />
              </dd>
              <dt>Category</dt>
              <dd>
                <Val v={[p.category, p.subcategory].filter(Boolean).join(' / ') || null} missing="not in file" />
              </dd>
              <dt>Tax category</dt>
              <dd>
                <Val v={p.taxCategory} missing="not in file" />
              </dd>
              <dt>Source system ID</dt>
              <dd className="mono small">
                <Val v={p.sourceItemId} missing="not in file" />
              </dd>
            </dl>
          </>
        ) : p.source === 'web' && p.webPage ? (
          <>
            <p className="muted small">
              <span className="badge">From Costco website</span> Not in the master list. Added on {fmtDate(p.createdAt)} from the{' '}
              <a href={p.webPage.url} target="_blank" rel="noreferrer">
                {p.webPage.sourceName} page ↗
              </a>{' '}
              read {fmtDate(p.webPage.fetchedAt)}. Values are as the page stated them.
            </p>
            <dl className="dl">
              <dt>Title on page</dt>
              <dd>
                <Val v={p.manifestTitle} />
              </dd>
              <dt>Costco price when read</dt>
              <dd>
                <Money amount={p.manifestPrice} currency={p.manifestCurrency} />
              </dd>
              <dt>Category on page</dt>
              <dd>
                <Val v={[p.category, p.subcategory].filter(Boolean).join(' › ') || null} />
              </dd>
            </dl>
          </>
        ) : (
          <p className="muted small">
            Created manually by {p.createdByName} on {fmtDate(p.createdAt)}. No manifest values.
          </p>
        )}
      </section>

      <section className="card">
        <div className="row between">
          <h2>Employee-entered values</h2>
          {!editing && (
            <button className="btn btn-small" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
        {editing ? (
          <ProductEdit p={p} onDone={(np) => (np && setProduct(np), setEditing(false))} />
        ) : (
          <dl className="dl">
            <dt>Listing title</dt>
            <dd>
              <Val v={p.listingTitle} />
              {p.listingTitleSourcePageId && <span className="badge">from Costco website</span>}
            </dd>
            <dt>Reference price</dt>
            <dd>
              <Money amount={p.referencePrice} currency={p.referenceCurrency} />
            </dd>
            <dt>Description</dt>
            <dd className="prewrap">
              <Val v={p.description} />
              {p.descriptionSourcePageId && <span className="badge">from Costco website</span>}
            </dd>
            {p.updatedByName && (
              <>
                <dt>Last changed</dt>
                <dd>
                  {fmtDate(p.updatedAt)} by {p.updatedByName}
                </dd>
              </>
            )}
          </dl>
        )}
        <p className="muted small">Changing these does not change lots that were already created from this product.</p>
      </section>

      <section className="card">
        <h2>From Costco websites</h2>
        <WebInfo product={p} onApplied={() => qc.invalidateQueries({ queryKey: ['product', id] })} />
      </section>

      <section className="card">
        <h2>Product barcodes (UPC / EAN)</h2>
        <p className="muted small">Only link a barcode you scanned from this actual product. The item number is not assumed to be a barcode.</p>
        {p.barcodes && p.barcodes.length > 0 ? (
          <ul className="plain">
            {p.barcodes.map((b) => (
              <li key={b.id}>
                <span className="mono">{b.barcode}</span> <span className="muted small">
                  {b.source === 'import' ? 'from the import file' : `linked by ${b.createdByName}`}, {fmtDate(b.createdAt)}
                </span>{' '}
                <button className="btn btn-small btn-ghost" onClick={() => removeBarcode(b.id, b.barcode)}>
                  Unlink
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="missing">No barcodes linked.</p>
        )}
        <ScanField kind="product" label="LINK A PRODUCT BARCODE" onScan={addBarcode} busy={bcBusy} />
        {msg && <Success>{msg}</Success>}
        <ErrorBox error={bcError} />
      </section>

      <section className="card">
        <h2>Lots using this product ({lots.data?.total ?? '…'})</h2>
        {lots.data?.lots.length ? (
          <ul className="plain">
            {lots.data.lots.map((l) => (
              <li key={l.id}>
                <Link className="mono" to={`/lots/${l.id}`}>
                  {l.lotNumber}
                </Link>{' '}
                {l.colour ?? ''} {l.conditionLabel ?? ''} · {l.locationCode ?? 'no location'} {l.status === 'void' && <span className="badge badge-bad">void</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">None yet.</p>
        )}
        <Link to={`/lots/new`} className="btn btn-small">
          Create a lot
        </Link>
        <span className="muted small"> (search for item {p.itemNumber} on the next screen)</span>
      </section>
    </div>
  );
}

function ProductEdit({ p, onDone }: { p: Product; onDone: (p: Product | null) => void }) {
  const settings = useSettings();
  const [f, setF] = useState({
    listingTitle: p.listingTitle ?? '',
    referencePrice: p.referencePrice ?? '',
    referenceCurrency: p.referenceCurrency ?? settings.data?.defaultCurrency ?? 'CAD',
    description: p.description ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const inp = (k: keyof typeof f) => ({ value: f[k], onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value }) });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ product: Product }>(`/products/${p.id}`, {
        method: 'PATCH',
        body: {
          version: p.version,
          listingTitle: f.listingTitle || null,
          referencePrice: f.referencePrice || null,
          referenceCurrency: f.referencePrice ? f.referenceCurrency : null,
          description: f.description || null,
        },
      });
      onDone(r.product);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save}>
      <Field label="Listing title" hint="Leave blank to keep using the manifest title. Do not add claims you have not verified.">
        <input {...inp('listingTitle')} maxLength={500} />
      </Field>
      <div className="grid2">
        <Field label="Reference price" hint="Blank = unknown (never treated as zero)">
          <input {...inp('referencePrice')} inputMode="decimal" />
        </Field>
        <Field label="Currency">
          <select {...inp('referenceCurrency')}>
            {[...new Set(['CAD', 'USD', f.referenceCurrency])].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Description (optional)">
        <textarea {...inp('description')} rows={4} />
      </Field>
      <ErrorBox error={error} />
      <div className="row gap">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => onDone(null)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
